/**
 * SPA のパス定数。単一情報源にして文字列を散らさない。
 *
 * 規約: `/v1.0` と `/openid` 配下は backend API 専用なので SPA ルートに使わない
 * （dev の vite proxy / 本番の CloudFront behavior がそこを backend へ転送する）。
 */
export const ROUTES = {
  home: "/",
  myApplications: "/my-applications",
  newApplication: "/my-applications/new",
  applicationType: "/my-applications/new/:modifiedModelId",
  approvalRequests: "/approval-requests",
  approvalReview: "/approval-requests/:caseId/:taskGuid",
  notifications: "/notifications",
  allCases: "/all-cases",
  permissions: "/permissions",
  activityLog: "/activity-log",
  processDesign: "/process-design",
  /**
   * プロセス設計（モデルを選んだ状態）。任意パラメータ 1 つのルートにして
   * `/process-design` と同じ画面に当てる（別ルートに分けると選ぶたびに再マウントされ、
   * 検索語や開閉が消える）。
   */
  processDesignModel: "/process-design/:modifiedModelId?",
  /** 旧「フォーム設計」。プロセス設計に統合したのでリダイレクトだけ残す。 */
  formDesign: "/form-design",
  secrets: "/secrets-config",
  caseProgress: "/cases/:caseId/progress",
  caseDetail: "/cases/:modifiedModelId/:caseId",
  taskRun: "/tasks/:caseId/:taskGuid",
  login: "/login",
  forbidden: "/forbidden",
} as const;

export function caseProgressPath(caseId: number): string {
  return `/cases/${caseId}/progress`;
}

export function taskRunPath(caseId: number, taskGuid: string): string {
  return `/tasks/${caseId}/${taskGuid}`;
}

/**
 * 案件種別の詳細（内容を確認してから作成する画面）。モデル id はスラッシュを
 * 含むため、API と同じ modified 形（`:` 区切り）で URL に載せる。
 */
export function applicationTypePath(processModelId: string): string {
  return `/my-applications/new/${processModelId.replace(/\//g, ":")}`;
}

/**
 * プロセス設計で 1 つのモデルを開く URL。
 * モデル id はスラッシュを含むので API と同じ modified 形（`:` 区切り）にする。
 */
export function processDesignModelPath(processModelId: string): string {
  return `/process-design/${processModelId.replace(/\//g, ":")}`;
}

/** 承認の判定画面（一覧から遷移する）。 */
export function approvalReviewPath(caseId: number, taskGuid: string): string {
  return `/approval-requests/${caseId}/${taskGuid}`;
}

/**
 * 案件詳細のページ。詳細 API がモデル id を要求するので URL に含める
 * （含めないと id だけで開けず、リンクの共有もできない）。
 */
export function caseDetailPath(caseId: number, processModelId: string): string {
  return `/cases/${processModelId.replace(/\//g, ":")}/${caseId}`;
}

/** 案件詳細の「この案件への対応」（操作パネル）の hash。 */
export const CASE_ACTION_HASH = "#case-action";

/**
 * 案件詳細を**操作パネルの位置で**開く。
 *
 * 下書き・差し戻しの再提出は詳細ページで完結させる（同じ操作の画面を 2 つ
 * 持たない）。ただし詳細は概要・図・履歴と情報が多いので、操作しに来た人には
 * パネルの位置を示す。
 */
export function caseActionPath(caseId: number, processModelId: string): string {
  return `${caseDetailPath(caseId, processModelId)}${CASE_ACTION_HASH}`;
}
